/**
 * Asset Cache Service for HomieAI Frontend
 *
 * Provides comprehensive caching for:
 * - Chapter PDFs
 * - Video files and annotations
 * - External assets (thumbnails, images, etc.)
 * - YouTube video metadata and thumbnails
 */

class AssetCacheService {
  constructor() {
    this.cacheName = 'homieai-assets-v1';
    this.maxCacheSize = 500 * 1024 * 1024; // 500MB
    this.cacheTTLs = {
      pdf: 7 * 24 * 60 * 60 * 1000, // 7 days
      video: 30 * 24 * 60 * 60 * 1000, // 30 days
      thumbnail: 24 * 60 * 60 * 1000, // 1 day
      annotation: 60 * 60 * 1000, // 1 hour
      metadata: 6 * 60 * 60 * 1000, // 6 hours
    };
  }

  /**
   * Generate a unique cache key for an asset
   */
  generateCacheKey(url, assetType, params = {}) {
    const keyComponents = [url, assetType];
    Object.keys(params).sort().forEach(key => {
      keyComponents.push(`${key}:${params[key]}`);
    });
    const keyString = keyComponents.join('|');
    return `asset_${assetType}_${btoa(keyString).replace(/[^a-zA-Z0-9]/g, '')}`;
  }

  /**
   * Check if cached item is still valid
   */
  async isCacheValid(cacheKey, assetType) {
    try {
      const cache = await caches.open(this.cacheName);
      const response = await cache.match(cacheKey);

      if (!response) return false;

      const cachedAt = new Date(response.headers.get('sw-cached-at') || 0).getTime();
      const ttl = this.cacheTTLs[assetType] || 3600000; // 1 hour default
      const now = Date.now();

      return (now - cachedAt) < ttl;
    } catch (error) {
      console.warn('Cache validation error:', error);
      return false;
    }
  }

  /**
   * Get asset from cache
   */
  async getCachedAsset(url, assetType, params = {}) {
    const cacheKey = this.generateCacheKey(url, assetType, params);

    if (!(await this.isCacheValid(cacheKey, assetType))) {
      return null;
    }

    try {
      const cache = await caches.open(this.cacheName);
      const response = await cache.match(cacheKey);

      if (response) {
        console.log(`Cache hit for ${assetType}:`, url);
        return await response.arrayBuffer();
      }
    } catch (error) {
      console.warn('Cache retrieval error:', error);
    }

    return null;
  }

  /**
   * Cache an asset
   */
  async cacheAsset(url, assetType, data, params = {}) {
    const cacheKey = this.generateCacheKey(url, assetType, params);

    try {
      const cache = await caches.open(this.cacheName);

      // Create response with metadata
      const response = new Response(data, {
        headers: {
          'Content-Type': this.getContentType(assetType),
          'Content-Length': data.byteLength.toString(),
          'sw-cached-at': new Date().toISOString(),
          'sw-asset-type': assetType,
          'sw-original-url': url,
        },
      });

      await cache.put(cacheKey, response);
      console.log(`Cached ${assetType}:`, url);

      // Clean up old cache entries
      await this.cleanupCache();

    } catch (error) {
      console.error('Cache storage error:', error);
    }
  }

  /**
   * Get content type for asset type
   */
  getContentType(assetType) {
    const contentTypes = {
      pdf: 'application/pdf',
      video: 'video/mp4',
      thumbnail: 'image/jpeg',
      annotation: 'application/json',
      metadata: 'application/json',
    };
    return contentTypes[assetType] || 'application/octet-stream';
  }

  /**
   * Download and cache asset
   */
  async downloadAndCacheAsset(url, assetType, headers = {}, params = {}) {
    // Check cache first
    const cachedData = await this.getCachedAsset(url, assetType, params);
    if (cachedData) {
      return cachedData;
    }

    try {
      console.log(`Downloading ${assetType}:`, url);

      const response = await fetch(url, {
        headers: {
          ...headers,
          'Cache-Control': 'no-cache', // Force fresh download
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.arrayBuffer();

      // Cache the downloaded data
      await this.cacheAsset(url, assetType, data, params);

      return data;

    } catch (error) {
      console.error(`Failed to download ${assetType}:`, error);
      return null;
    }
  }

  /**
   * Cache PDF asset
   */
  async cachePDF(pdfUrl, authToken = null) {
    // DISABLED: Bypassing browser-side PDF caching to resolve 504 errors and redundant downloads.
    // This satisfies the request to remove the cache feature for PDFs.
    return null;

    /*
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    return await this.downloadAndCacheAsset(pdfUrl, 'pdf', headers);
    */
  }

  /**
   * Cache video asset
   */
  async cacheVideo(videoUrl) {
    return await this.downloadAndCacheAsset(videoUrl, 'video');
  }

  /**
   * Cache thumbnail asset
   */
  async cacheThumbnail(thumbnailUrl) {
    return await this.downloadAndCacheAsset(thumbnailUrl, 'thumbnail');
  }

  /**
   * Cache video annotation
   */
  async cacheVideoAnnotation(videoId, annotationData) {
    const data = new TextEncoder().encode(JSON.stringify(annotationData));
    await this.cacheAsset(`annotation:${videoId}`, 'annotation', data);
  }

  /**
   * Get cached video annotation
   */
  async getCachedVideoAnnotation(videoId) {
    const data = await this.getCachedAsset(`annotation:${videoId}`, 'annotation');
    if (data) {
      try {
        return JSON.parse(new TextDecoder().decode(data));
      } catch (error) {
        console.warn('Failed to parse cached annotation:', error);
      }
    }
    return null;
  }

  /**
   * Cache YouTube metadata
   */
  async cacheYouTubeMetadata(videoId, metadata) {
    const data = new TextEncoder().encode(JSON.stringify(metadata));
    await this.cacheAsset(`youtube:${videoId}`, 'metadata', data);
  }

  /**
   * Get cached YouTube metadata
   */
  async getCachedYouTubeMetadata(videoId) {
    const data = await this.getCachedAsset(`youtube:${videoId}`, 'metadata');
    if (data) {
      try {
        return JSON.parse(new TextDecoder().decode(data));
      } catch (error) {
        console.warn('Failed to parse cached metadata:', error);
      }
    }
    return null;
  }

  /**
   * Clean up old cache entries
   */
  async cleanupCache() {
    try {
      const cache = await caches.open(this.cacheName);
      const keys = await cache.keys();

      let totalSize = 0;
      const entries = [];

      // Get all cache entries with their metadata
      for (const request of keys) {
        try {
          const response = await cache.match(request);
          if (response) {
            const contentLength = parseInt(response.headers.get('Content-Length') || '0');
            const cachedAt = new Date(response.headers.get('sw-cached-at') || 0).getTime();
            const assetType = response.headers.get('sw-asset-type') || 'unknown';

            entries.push({
              request,
              size: contentLength,
              cachedAt,
              assetType,
            });

            totalSize += contentLength;
          }
        } catch (error) {
          console.warn('Error reading cache entry:', error);
        }
      }

      // If cache is too large, remove oldest entries
      if (totalSize > this.maxCacheSize) {
        console.log(`Cache size ${totalSize / 1024 / 1024}MB exceeds limit, cleaning up...`);

        // Sort by cache time (oldest first)
        entries.sort((a, b) => a.cachedAt - b.cachedAt);

        let sizeToRemove = totalSize - (this.maxCacheSize * 0.8); // Leave 20% free
        let removedCount = 0;

        for (const entry of entries) {
          if (sizeToRemove <= 0) break;

          await cache.delete(entry.request);
          sizeToRemove -= entry.size;
          removedCount++;
        }

        console.log(`Removed ${removedCount} old cache entries`);
      }

    } catch (error) {
      console.error('Cache cleanup error:', error);
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    try {
      const cache = await caches.open(this.cacheName);
      const keys = await cache.keys();

      const stats = {
        totalEntries: keys.length,
        totalSize: 0,
        byType: {},
      };

      for (const request of keys) {
        try {
          const response = await cache.match(request);
          if (response) {
            const contentLength = parseInt(response.headers.get('Content-Length') || '0');
            const assetType = response.headers.get('sw-asset-type') || 'unknown';

            stats.totalSize += contentLength;

            if (!stats.byType[assetType]) {
              stats.byType[assetType] = { count: 0, size: 0 };
            }
            stats.byType[assetType].count++;
            stats.byType[assetType].size += contentLength;
          }
        } catch (error) {
          // Ignore errors for individual entries
        }
      }

      // Convert bytes to MB
      stats.totalSizeMB = (stats.totalSize / 1024 / 1024).toFixed(2);
      Object.keys(stats.byType).forEach(type => {
        stats.byType[type].sizeMB = (stats.byType[type].size / 1024 / 1024).toFixed(2);
      });

      return stats;

    } catch (error) {
      console.error('Error getting cache stats:', error);
      return { error: error.message };
    }
  }

  /**
   * Clear cache for specific asset type or all
   */
  async clearCache(assetType = null) {
    try {
      if (assetType) {
        // Clear specific asset type
        const cache = await caches.open(this.cacheName);
        const keys = await cache.keys();

        let removedCount = 0;
        for (const request of keys) {
          try {
            const response = await cache.match(request);
            if (response) {
              const entryAssetType = response.headers.get('sw-asset-type');
              if (entryAssetType === assetType) {
                await cache.delete(request);
                removedCount++;
              }
            }
          } catch (error) {
            // Ignore errors for individual entries
          }
        }

        console.log(`Cleared ${removedCount} ${assetType} cache entries`);

      } else {
        // Clear all caches
        await caches.delete(this.cacheName);
        console.log('Cleared all asset caches');
      }

    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  /**
   * Prefetch multiple assets
   */
  async prefetchAssets(assetUrls, assetType, headers = {}) {
    const promises = assetUrls.map(url =>
      this.downloadAndCacheAsset(url, assetType, headers)
    );

    try {
      await Promise.allSettled(promises);
      console.log(`Prefetched ${assetUrls.length} ${assetType} assets`);
    } catch (error) {
      console.warn('Prefetch error:', error);
    }
  }

  /**
   * Cache PDF page images for faster loading
   */
  async cachePdfPageImage(pdfUrl, pageNumber, imageData, scale = 1.0) {
    try {
      const cacheKey = this.generateCacheKey(pdfUrl, 'pdf_page', {
        page: pageNumber,
        scale: scale.toFixed(2)
      });

      const cache = await caches.open(this.cacheName);
      const response = new Response(imageData, {
        headers: {
          'Content-Type': 'image/png',
          'sw-cached-at': new Date().toISOString(),
          'sw-asset-type': 'pdf_page',
          'sw-pdf-url': pdfUrl,
          'sw-page-number': pageNumber.toString(),
          'sw-scale': scale.toString()
        }
      });

      await cache.put(cacheKey, response);
      console.log(`Cached PDF page ${pageNumber} for ${pdfUrl}`);
    } catch (error) {
      console.warn('Error caching PDF page image:', error);
    }
  }

  /**
   * Get cached PDF page image
   */
  async getCachedPdfPageImage(pdfUrl, pageNumber, scale = 1.0) {
    try {
      const cacheKey = this.generateCacheKey(pdfUrl, 'pdf_page', {
        page: pageNumber,
        scale: scale.toFixed(2)
      });

      const cache = await caches.open(this.cacheName);
      const response = await cache.match(cacheKey);

      if (response && this.isCacheValid(cacheKey, 'pdf_page')) {
        console.log(`Using cached PDF page ${pageNumber} for ${pdfUrl}`);
        return response;
      }
    } catch (error) {
      console.warn('Error retrieving cached PDF page image:', error);
    }
    return null;
  }

  /**
   * Preload PDF pages for better performance
   */
  async preloadPdfPages(pdfUrl, totalPages, currentPage = 1, preloadCount = 2) {
    const pagesToPreload = [];

    // Preload pages around current page
    for (let i = Math.max(1, currentPage - preloadCount);
      i <= Math.min(totalPages, currentPage + preloadCount);
      i++) {
      if (i !== currentPage) { // Don't preload current page again
        pagesToPreload.push(i);
      }
    }

    // Use requestIdleCallback if available for non-blocking preloading
    const preloadPage = async (pageNum) => {
      // Disabled to resolve 504 Gateway Timeout issues
      return;
      /*
      try {
        // Check if already cached
        const cached = await this.getCachedPdfPageImage(pdfUrl, pageNum);
        if (!cached) {
          // Trigger loading by making a request to cache endpoint
          const cacheUrl = `/api/core/cache/pdf/?url=${encodeURIComponent(pdfUrl)}&page=${pageNum}&preload=true`;
          await fetch(cacheUrl, { method: 'HEAD' }); // HEAD request to trigger caching
        }
      } catch (error) {
        // Ignore preload errors
      }
      */
    };

    if ('requestIdleCallback' in window) {
      pagesToPreload.forEach(pageNum => {
        requestIdleCallback(() => preloadPage(pageNum), { timeout: 2000 });
      });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        pagesToPreload.forEach(pageNum => preloadPage(pageNum));
      }, 100);
    }
  }
}

// Create singleton instance
const assetCacheService = new AssetCacheService();

export default assetCacheService;